import { apiHelpers } from './api';

/**
 * TDD Service
 * Handles all API communication for TDD workflow
 */
class TDDService {
  /**
   * Initialize TDD workspace for a project
   */
  static async initializeWorkspace(projectId) {
    try {
      const response = await apiHelpers.post(
        `/tdd-development/projects/${projectId}/tdd/initialize`
      );
      return response.data;
    } catch (error) {
      console.error('Failed to initialize TDD workspace:', error);
      throw error;
    }
  }

  /**
   * Create TDD workspace with specific configuration
   */
  static async createWorkspace(projectId, config) {
    try {
      const response = await apiHelpers.post(
        `/tdd-development/projects/${projectId}/tdd/workspace/create`,
        config
      );
      return response.data;
    } catch (error) {
      console.error('Failed to create TDD workspace:', error);
      throw error;
    }
  }

  /**
   * Get current workspace status
   */
  static async getWorkspaceStatus(projectId) {
    try {
      const response = await apiHelpers.get(
        `/tdd-development/projects/${projectId}/tdd/workspace/status`
      );
      return response.data;
    } catch (error) {
      console.error('Failed to get workspace status:', error);
      throw error;
    }
  }

  /**
   * Execute a TDD cycle for a specific task
   */
  static async executeTDDCycle(projectId, taskId, taskData) {
    try {
      const response = await apiHelpers.post(
        `/tdd-development/projects/${projectId}/tdd/tasks/${taskId}/execute`,
        taskData
      );
      return response.data;
    } catch (error) {
      console.error('Failed to execute TDD cycle:', error);
      throw error;
    }
  }

  /**
   * Get current progress status
   */
  static async getProgress(projectId) {
    try {
      const response = await apiHelpers.get(
        `/tdd-development/projects/${projectId}/tdd/progress`
      );
      return response.data;
    } catch (error) {
      console.error('Failed to get TDD progress:', error);
      throw error;
    }
  }

  /**
   * Analyze user stories for TDD tasks
   */
  static async analyzeUserStories(projectId, workspacePath) {
    try {
      const response = await apiHelpers.post(
        `/tdd-development/projects/${projectId}/tdd/stories/analyze`,
        { workspace_path: workspacePath }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to analyze user stories:', error);
      throw error;
    }
  }

  /**
   * Break down task into atomic components
   */
  static async getNextTask(projectId) {
    try {
      const response = await apiHelpers.get(
        `/tdd-development/projects/${projectId}/tdd/tasks/next`
      );
      return response.data;
    } catch (error) {
      console.error('Failed to get next task:', error);
      throw error;
    }
  }

  /**
   * Validate task completion
   */
  static async validateTaskCompletion(projectId, taskId, validationData) {
    try {
      const response = await apiHelpers.post(
        `/tdd-development/projects/${projectId}/tdd/tasks/${taskId}/complete`,
        validationData
      );
      return response.data;
    } catch (error) {
      console.error('Failed to validate task completion:', error);
      throw error;
    }
  }

  /**
   * Get workspace files
   */
  static async getWorkspaceFiles(projectId) {
    try {
      const response = await apiHelpers.get(
        `/tdd-development/projects/${projectId}/tdd/workspace/files`
      );
      return response.data;
    } catch (error) {
      console.error('Failed to get workspace files:', error);
      throw error;
    }
  }

  /**
   * Get specific file content
   */
  static async getFileContent(projectId, filePath) {
    try {
      const response = await apiHelpers.get(
        `/tdd-development/projects/${projectId}/tdd/workspace/files/${filePath}`
      );
      return response.data;
    } catch (error) {
      console.error('Failed to get file content:', error);
      throw error;
    }
  }

  /**
   * Update file content
   */
  static async updateFileContent(projectId, filePath, content) {
    try {
      const response = await apiHelpers.post(
        `/tdd-development/projects/${projectId}/tdd/workspace/files/${filePath}`,
        { content }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to update file content:', error);
      throw error;
    }
  }

  /**
   * Validate TDD readiness
   */
  static async validateTDDReadiness(projectId) {
    try {
      const response = await apiHelpers.post(
        `/tdd-development/projects/${projectId}/tdd/validate-readiness`
      );
      return response.data;
    } catch (error) {
      console.error('Failed to validate TDD readiness:', error);
      throw error;
    }
  }

  /**
   * Check TDD service health
   */
  static async checkHealth() {
    try {
      const response = await apiHelpers.get(
        `/tdd-development/tdd/health`
      );
      return response.data;
    } catch (error) {
      console.error('Failed to check TDD service health:', error);
      throw error;
    }
  }
}

export default TDDService;